<script lang="ts" setup>
import type { CatalogPageData } from '~/types/Catalog';

const props = defineProps<{ pageData: CatalogPageData }>();
const pageData = toRef(props, 'pageData');

onMounted(async () => {
  if (import.meta.client) {
    if (window.r46) window.r46('track', 'category', pageData.value.slug);
  }
});

const route = useRoute();
const { appUrl } = useRuntimeConfig().public;

const seoText = computed(() => getCatalogSeoText(pageData.value.seo_data));
const seoDescription = computed(() => getCatalogSeoDescription(pageData.value.seo_data));
const seoIndexing = computed(() =>
  pageData.value.seo_data?.canonical_path
    ? getCatalogSeoIndexing(pageData.value.seo_data.canonical_path, route.query, pageData.value.seo_data.robots)
    : null,
);
const canonicalUrl = computed(() =>
  seoIndexing.value ? new URL(seoIndexing.value.canonicalPath, appUrl).toString() : null,
);

useHead(() => ({
  title: pageData.value.seo_data?.title,
  link: canonicalUrl.value ? [{ key: 'canonical', rel: 'canonical', href: canonicalUrl.value }] : [],
  meta: [
    { name: 'description', content: pageData.value.seo_data?.description },
    ...(seoIndexing.value ? [{ name: 'robots', content: seoIndexing.value.robots }] : []),
    { property: 'og:title', content: pageData.value.seo_data?.title },
    { property: 'og:description', content: pageData.value.seo_data?.description },
    { property: 'og:type', content: 'category' },
    { property: 'og:url', content: canonicalUrl.value ?? route.fullPath },
    { property: 'og:site_name', content: 'Wtfish.ru' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:image', content: pageData.value.offers[0]?.offer_images[0]?.url },
  ],
}));

const availableFilters = computed(() =>
  pageData.value.filters.filter((filter) => !pageData.value.locked_filters?.includes(filter.slug)),
);
const { activeFilters, fastPropertyFilter } = useCatalogFilters(availableFilters.value);

function scrollToProductsListing() {
  const productsListingAnchor = document.getElementById('products');

  if (productsListingAnchor) {
    return navigateTo({
      path: route.path,
      query: { ...route.query, 'filter[marketing_badge_slugs]': 'sale' },
      hash: '#products',
    });
  }
}
</script>

<template>
  <div class="category-page">
    <CatalogBreadcrumbsBlock :breadcrumbs="pageData.breadcrumbs" />

    <div class="flex items-center gap-2">
      <BackButton />

      <h1>
        {{ pageData.seo_data?.header || pageData.name }}
      </h1>
    </div>

    <section class="flex flex-col gap-6">
      <div
        v-if="pageData.sub_categories?.length"
        class="categories-grid"
      >
        <CategoryListItem
          v-for="category in pageData.sub_categories"
          :key="category.slug"
          :category="category"
        />
      </div>

      <CategoryFastPropertyFilters
        v-if="fastPropertyFilter"
        :filter="fastPropertyFilter"
      />

      <div id="products" />

      <CategoryPageBanners
        v-if="pageData.banners.mobile && pageData.banners.desktop"
        :banners="pageData.banners"
      />

      <ProductsListing
        :active-filters="activeFilters"
        :filters="availableFilters"
        :offers="pageData.offers"
        :pagination="pageData.pagination"
        :schema="pageData.schema"
        :sort-options="pageData.sorting_options"
      />

      <FoldingTextBlock
        v-if="seoText"
        ignore-color-styles
        :max-lines="3"
        :text="seoText"
        class="text-sm"
      />

      <div
        v-else-if="seoDescription"
        class="category-page__seo-description text-sm"
        v-html="seoDescription"
      />

      <SeoCatalogPageLinks
        v-if="pageData.seo_data?.links?.length"
        :links="pageData.seo_data.links"
        class="mt-2"
      />
    </section>
  </div>
</template>

<style lang="scss" scoped>
.category-page {
  gap: 16px;

  @include inner-content;

  &__seo-description {
    line-height: 1.4;
  }
}
</style>
